import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  BehaviorSubject,
  Observable,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { LocaleDatePipe } from 'src/core/pipes';
import { DialogService } from 'src/core/services';
import { AuthStore } from 'src/core/stores';
import {
  CoreUsersService,
  CreateUserDto,
  GetUsers200Response,
  GetUsersRequestParams,
  UserDto,
} from 'src/generated/api/nestjs-backend';
import { AvatarComponent } from '../shared/avatar/avatar.component';
import { SidebarComponent } from '../shared/sidebar/sidebar.component';
import { TableComponent } from '../shared/table/table.component';
import { ActivateUserDialogComponent } from './activate-user-dialog/activate-user-dialog.component';
import { EditUserDialogComponent } from './edit-user-dialog/edit-user-dialog.component';

@Component({
  selector: 'core-user-management',
  templateUrl: './user-management.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoDirective,
    TableComponent,
    AvatarComponent,
    LocaleDatePipe,
  ],
})
export class UserManagementComponent {
  private authStore = inject(AuthStore);
  private dialogService = inject(DialogService);
  private coreUsersService = inject(CoreUsersService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(SidebarComponent) sidebar!: SidebarComponent;

  private isLoading$$ = new BehaviorSubject<boolean>(true);
  isLoading$ = this.isLoading$$.asObservable();

  private getUsersRequestParams$$ = new BehaviorSubject<GetUsersRequestParams>({
    xTenantSlug: this.authStore.tenantSlug(),
    page: 1,
    size: 8,
    sortBy: 'lastName',
    sortOrder: 'asc',
    status: 'any',
  });
  getUsersRequestParams$ = this.getUsersRequestParams$$.asObservable();

  getUsers200Response$: Observable<GetUsers200Response>;

  private destroy$ = new Subject<void>();
  tableHasCreateButton = true;
  selectedRows: boolean[] = [];
  selectAllState: boolean = false;
  tableColumns = [
    { label: 'Name', key: 'firstName' },
    { label: 'Email', key: 'email' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Active', key: 'isActive' },
  ];

  constructor() {
    this.getUsers200Response$ = this.getUsers200ResponseObservable();
  }

  changeRequest(request: Omit<GetUsersRequestParams, 'xTenantSlug'>) {
    this.getUsersRequestParams$$.next({
      ...this.getUsersRequestParams$$.value,
      ...request,
    });
  }

  changePage(page: number) {
    this.changeRequest({ page });
  }

  toggleSelectAll() {
    this.selectAllState = !this.selectAllState;
    this.selectedRows = this.selectedRows.map(() => this.selectAllState);
  }

  getPages(totalPages: number): number[] {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  openCreateModal() {
    // TODO Change hardcoded creation of new Users
    const newUser: CreateUserDto = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    };

    const modalRef = this.dialogService.open(EditUserDialogComponent, {
      context: { user: newUser, createOrEditDialog: true },
    });

    this.openModal(modalRef);
  }

  openEditModal(userItem: UserDto) {
    const modalRef = this.dialogService.open(EditUserDialogComponent, {
      context: { user: userItem, createOrEditDialog: true },
    });

    this.openModal(modalRef);
  }

  deleteUser(userItem: UserDto): void {
    const modalRef = this.dialogService.open(EditUserDialogComponent, {
      context: { user: userItem, createOrEditDialog: false },
    });

    this.openModal(modalRef);
  }

  toggleUserActiveOrInactive(userItem: UserDto, event: MouseEvent): void {
    event.preventDefault();
    const originalIsActive = userItem.isActive;
    userItem.isActive = originalIsActive;

    const modalRef = this.dialogService.open(ActivateUserDialogComponent, {
      context: { user: userItem },
    });
    modalRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((success: unknown) => {
        if (success) {
          userItem.isActive = !originalIsActive;
          this.cdr.detectChanges();
        } else {
          userItem.isActive = originalIsActive;
          this.cdr.detectChanges();
        }
        modalRef.componentRef.destroy();
      });
  }

  openModal(modalRef: {
    onClose: Observable<unknown>;
    componentRef: ComponentRef<unknown>;
  }) {
    modalRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((success: unknown) => {
        if (success) {
          this.getUsersRequestParams$$.next(this.getUsersRequestParams$$.value);
        }
        modalRef.componentRef.destroy();
      });
  }

  private getUsers200ResponseObservable() {
    return this.getUsersRequestParams$$.pipe(
      tap(() => this.authStore.setIsLoading(true)),
      switchMap((getUsersRequestParams) =>
        this.coreUsersService.getUsers(getUsersRequestParams),
      ),
      tap(() => this.authStore.setIsLoading(false)),
    );
  }
}
